import React, { useState, useEffect, useRef } from 'react';
import {
  RadioButtonUnchecked,
  RadioButtonChecked,
} from "@mui/icons-material";
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeatureSource,
  CharacterRoll,
  CharacterSheetInterface,
  RollPlus,
  RolledValue,
} from "@dndModels";
import { 
  ABILITY_SCORES
} from "@dndModels/Constants";
import { AbilityScoreType } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBox";

import Roller from "@dndComponents/other/Roller";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterSavingThrowInput from "@dndComponents/character/saves/CharacterSavingThrowInput";

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  shared: boolean;
  vertical?: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterSavingThrows({
  character,
  char_interface,
  shared,
  vertical = false,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [popoverSave, setPopoverSave] = useState("");
  const [open, setOpen] = useState(false);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    setTimeout(() => {
      setReloading(true);
    }, 100);
  }
  
  useEffect(() => {
    character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderSavingThrowModifiers = () => {
    const advantages = renderSavingThrowAdvantages();
    const min_rolls = renderSavingThrowMinimumRolls();
    if (advantages || min_rolls) {
      return [
        advantages,
        min_rolls
      ];
    }
    return <div>You have no saving throw modifiers (or do you?)</div>;
  }

  const renderSavingThrowAdvantages = () => {
    const advantages = char_interface.advantages.filter(o => o.advantage.type === "Saving Throw");
    if (advantages.length > 0) {
      return (
        <Grid container spacing={0} direction="row" key="adv">
          { advantages.map((adv, key) => {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                { adv.advantage.disadvantage ? "DISADV" : "ADV" }on { adv.advantage.type_detail }Saving Throws { adv.advantage.formula }
              </Grid>
            );
          })}
        </Grid>
      );
    }
    return null;
  }

  const renderSavingThrowMinimumRolls = () => {
    const minimum_rolls: { ability: string, min: number, when: string }[] = [];
    ABILITY_SCORES.forEach(ability => {
      const save_minimum_rolls = char_util.getMinimumRollsWithConditions(char_interface, "Saving Throw", ability);
      save_minimum_rolls.forEach(min_roll => {
        minimum_rolls.push({ ability, min: char_util.value(min_roll.threshold, { obj: char_interface }), when: min_roll.when });
      });
    });
    
    if (minimum_rolls.length > 0) {
      return (
        <Grid container spacing={0} direction="row" key="min">
          { minimum_rolls.map((min_roll, key) => {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>Minimum { min_roll.min }for { min_roll.ability }&nbsp;{ min_roll.when }
              </Grid>
            );
          })}
        </Grid>
      );
    }
    return null;
  }

  const renderSavingThrow = (ability: string) => {
    const enabled = api.user && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id));
    const abbrev = common_util.ability_score_abbreviation(ability);
    const char_roll = char_interface.saving_throws[abbrev] as CharacterRoll;
    const roll_plus = new RollPlus();
    roll_plus.flat = char_roll.modifier;
    let bonuses_string = "";
    let modifier = char_roll.modifier;
    for (const bonus of char_roll.rolls) {
      const roll_plus2 = bonus;
      if (roll_plus2) {
        modifier += roll_plus2.flat;
        if (!["","None","undefined"].includes(roll_plus2.ability_score)) {
          const mod = char_interface.current_ability_scores.getModifier(roll_plus2.ability_score);
          if (mod) {
            modifier += mod;
          }
        }
        if (roll_plus2.size === 1) {
          modifier += roll_plus2.count;
        } else if (roll_plus2.size > 1 && roll_plus2.count !== 0) {
          roll_plus2.recalculate_string();
          if (roll_plus2.as_string.length > 0 && roll_plus2.as_string[0] !== "-") {
            bonuses_string += `+${roll_plus2.as_string}`;
          } else {
            bonuses_string += roll_plus2.as_string;
          }
        }
      }
    }
    if (modifier > 0) {
      bonuses_string = `+${modifier}${bonuses_string}`;
    } else if (modifier < 0) {
      bonuses_string = `${modifier}${bonuses_string}`;
    } else if (bonuses_string === "") {
      bonuses_string = "+0";
    }
    // if (ability === "DEX") {
    //   debugger;
    // }
    const { advantage, advantages, greater_advantage } = char_util.getAdvantage(char_interface, "Saving Throw", ability);
    const minimum_roll = char_util.getMinimumRoll(char_interface, "Saving Throw", ability);
    return (
      <Grid
        size={{
          xs: 12
        }}>
        <div className="curved_border" style={{
            padding: "4px"
          }}>
          <Grid container spacing={0} direction="row" style={{ margin: "4px" }}>
            <Grid
              size={{
                xs: 2
              }}>
              <div style={{ 
                float: "left", 
                lineHeight: "0.5" 
              }}>
                { char_roll.proficiency === 1 ? <RadioButtonChecked /> : <RadioButtonUnchecked />}
              </div>
            </Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 5
              }}>
              <Grid
                className="header centered"
                size={{
                  xs: 12
                }}>
                <div style={{ lineHeight: "2.2" }}>
                  { ability }
                </div>
              </Grid>
              { minimum_roll > 0 && 
                <Grid
                  className="centered"
                  style={{ 
                      color: "gray", 
                      fontSize: "11px",
                    }}
                  size={{
                    xs: 12
                  }}>
                  <div>
                    Minimum { minimum_roll }
                  </div>
                </Grid>
              }
            </Grid>
            <Grid
              size={{
                xs: 5
              }}>
              <DandDButtonBox 
                name={`${bonuses_string}`}
                disabled={ !enabled && !shared }
                advantage={ advantage }
                advantages={ advantages }
                greater_advantage={ greater_advantage }
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  const roll_plus = new RollPlus();
                  roll_plus.flat = char_roll.modifier;
                  let modifier = char_roll.modifier;
                  let rolls: RollPlus[] = [];
                  for (const bonus of char_roll.rolls) {
                    const roll_plus2 = bonus;
                    if (roll_plus2) {
                      modifier += roll_plus2.flat;
                      if (!["","None","undefined"].includes(roll_plus2.ability_score)) {
                        const mod = char_interface.current_ability_scores.getModifier(roll_plus2.ability_score);
                        if (mod) {
                          modifier += mod;
                        }
                      }
                      if (roll_plus2.size === 1) {
                        modifier += roll_plus2.count;
                      } else if (roll_plus2.size > 1 && roll_plus2.count !== 0) {
                        const new_rp = roll_plus2.clone();
                        new_rp.flat = 0;
                        new_rp.ability_score = "";
                        rolls.push(new_rp);
                        // roll_plus2.recalculate_string();
                        // if (roll_plus2.as_string.length > 0 && roll_plus2.as_string[0] !== "-") {
                        //   bonuses_string += `+${roll_plus2.as_string}`;
                        // } else {
                        //   bonuses_string += roll_plus2.as_string;
                        // }
                      }
                    }
                  }
                  roll_plus.flat = modifier;
                  rolls = [roll_plus,...rolls];
                  const { advantage, greater_advantage } = char_util.getAdvantage(char_interface, "Saving Throw", ability);
                  const abbrev = common_util.ability_score_abbreviation(ability) as AbilityScoreType;
                  const minimum_roll = char_util.getMinimumRoll(char_interface, "Saving Throw", ability);
                  // debugger;
                  // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Saving Throw")) && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(abbrev))).length > 0;
                  const new_roll = char_util.rollD20({ char_interface: character, rolls, type: "Saving Throw", use_ability_score: abbrev, advantage, greater_advantage, minimum_roll })
                  setRoll(new_roll);
                  setPopoverSave(ability);
                  setPopoverAnchorEl(event.currentTarget);
                }} 
              />
            </Grid>
          </Grid>
        </div>
      </Grid>
    );
  }

  const renderExtras = () => {
    return [
      <ModalOrDrawer key="drawer"
        open={ open } 
        // drawer_only
        onClose={() => {
          setOpen(false); 
          character.subscription_holder.modalClosed();
        }}>
        { open ? 
          <CharacterSavingThrowInput
            character={character}
          />
          :
          <span></span>
        }
      </ModalOrDrawer>,
      <Popover key="rolls"
        open={ popoverAnchorEl !== null && popoverSave !== "" }
        anchorEl={popoverAnchorEl}
        onClose={() => {
          setPopoverAnchorEl(null);
          setPopoverSave("");
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        { renderRoll() }
      </Popover>
    ];
  }

  const renderRoll = () => {
    if (popoverSave === "") {
      return null;
    } else {
      const rolls: RollPlus[] = [];
      const roll_plus = new RollPlus();
      roll_plus.ability_score = popoverSave;
      if (char_interface.proficiencies.filter(o => o.type === "Saving Throw" && o.proficiency_id === popoverSave).length > 0) {
        roll_plus.flat = +character.proficiency_modifier;
      }
      rolls.push(roll_plus);
      char_interface.saving_throw_bonuses.forEach(bonus => {
        if (bonus.types.length === 0 || bonus.types.includes(popoverSave)) {
          rolls.push(char_util.toRollPlus(bonus.rolls, { obj: char_interface }));
        }
      });
      return (
        <Roller 
          name={popoverSave}
          rolled_values={roll}
          type="Saving Throw" 
        />
      );
    }
  }

  return (
    <Grid container spacing={0} direction="row" 
      className="curved_border bg_black_transp">
      <Grid
        className={ shared ? "header centered" : "header clickable centered"}
        onClick={() => {
          if (!shared) {
            setOpen(true);
          }
        }}
        size={{
          xs: 12
        }}>
        <div>
          Saving Throws
        </div>
      </Grid>
      <Grid
        className="centered"
        style={{
          fontSize: "11px"
        }}
        size={{
          xs: 12
        }}>
        <div style={{ width: (vertical ? "95%" : "300px") }}>
          <Grid container spacing={0} direction="row">
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: vertical ? 12 : 6
              }}>
              { renderSavingThrow("STR") }
              { renderSavingThrow("DEX") }
              { renderSavingThrow("CON") }
            </Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: vertical ? 12 : 6
              }}>
              { renderSavingThrow("INT") }
              { renderSavingThrow("WIS") }
              { renderSavingThrow("CHA") }
            </Grid>
          </Grid>
        </div>
      </Grid>
      <Grid
        className="centered"
        style={{
          fontSize: "11px"
        }}
        size={{
          xs: 12
        }}>
        <div>
          { renderSavingThrowModifiers() }
        </div>
      </Grid>
      { renderExtras() }
    </Grid>
  );
}
